import React, { useEffect, useMemo, useState } from "react";
import { FaArchive, FaSearch, FaUserFriends } from "react-icons/fa";
import { apihubGet } from "../../api/apihub";
import { useProjectPhone } from "../../hooks/useProjectPhone";

const ArchivedContacts = () => {
  const { phoneNoId } = useProjectPhone();
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await apihubGet(
          `/getInteractedCustomerDetail/getarchivedcontactsdetailpaginated/${phoneNoId}?page=${page}&limit=${perPage}`,
        );
        if (!cancelled) {
          setCustomers(res?.customers || []);
          setTotal(res?.total ?? 0);
        }
      } catch {
        if (!cancelled) {
          setCustomers([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phoneNoId, page, perPage]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) ||
        String(c.waid || c.mobile || "").toLowerCase().includes(q),
    );
  }, [customers, query]);

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="archived-page">
      <div className="archived-topstrip">
        <div className="archived-title">
          <FaArchive />
          <div>
            <h1>Archived Contacts</h1>
            <p>
              Delete by Archiving leads that are JUNK and don&apos;t want to use
              and keep sales and support teams focused.
              <br />
              <span className="archived-note">
                Note: Archived leads can be restored if you want them in future.
              </span>
            </p>
          </div>
        </div>

        <div className="archived-strip-metrics">
          <div className="contacts-metric">
            <span>Quality Rating</span>
            <span className="contacts-pill contacts-pill-green">GREEN</span>
          </div>
          <div className="contacts-metric">
            <span>Template Messaging Tier</span>
            <span className="contacts-pill contacts-pill-dark">TIER_100K</span>
          </div>
          <div className="contacts-metric">
            <span>Remaining Quota</span>
            <span className="contacts-pill contacts-pill-dark">100000</span>
          </div>
        </div>
      </div>

      <div className="archived-feature">
        <div className="archived-feature-card">
          <div>
            <h2>Feature Overview</h2>
            <p>
              Access archived contacts, build audiences, and launch campaigns-all
              from one place.
            </p>
          </div>
          <div className="archived-total-pill">
            Total Archived Contacts: {total}
          </div>
        </div>
      </div>

      <div className="archived-body">
        <div className="archived-toolbar">
          <div className="archived-search">
            <FaSearch className="archived-search-icon" />
            <input
              type="search"
              placeholder="Search by name or phone number"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="archived-actions">
            <button type="button" className="archived-action" disabled>
              UnArchived
            </button>
            <button type="button" className="archived-action" disabled>
              Export Contact
            </button>
          </div>
        </div>

        {loading ? (
          <p className="archived-empty">Loading archived contacts…</p>
        ) : filtered.length === 0 ? (
          <p className="archived-empty">No customers found</p>
        ) : (
          <div className="biz-table-wrap archived-table-wrap">
            <table className="biz-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Archived</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c._id || c.waid}>
                    <td>{c.name || "—"}</td>
                    <td>{c.waid || c.mobile || "—"}</td>
                    <td>
                      {c.archivedAt
                        ? new Date(c.archivedAt).toLocaleDateString()
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="archived-pagination">
          <div className="archived-pagination-left">
            <span>Show:</span>
            <select
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={10}>10 / page</option>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
            </select>
          </div>
          <div className="archived-pagination-right">
            <button
              type="button"
              className="archived-page-btn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Previous
            </button>
            <span className="archived-page-indicator">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="archived-page-btn archived-page-btn-active"
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <FaUserFriends className="archived-hidden-icon" aria-hidden="true" />
    </div>
  );
};

export default ArchivedContacts;
